import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
  UserCheck,
  Plus,
  Search,
  Filter,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Award,
  Users,
  Clock,
  Edit,
  MoreVertical,
  Shield,
  User,
  Trash2,
  Settings,
  X
} from 'lucide-react';
import { useEmpresa } from '../contexts/EmpresaContext';
import { useSelector } from 'react-redux';
import api from '../api/config';
import TeamModal from './TeamModal';
import CatalogModal from './CatalogModal';
import PermissionsModal from './PermissionsModal';
import CustomSelect from './CustomSelect';
import '../styles/Equipo.css';

const Equipo = () => {
  const location = useLocation();
  const highlightedMemberRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('todos');
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [selectedMember, setSelectedMember] = useState(null);
  const [catalogModalOpen, setCatalogModalOpen] = useState(false);
  const [permissionsModalOpen, setPermissionsModalOpen] = useState(false);
  const [selectedMemberForPermissions, setSelectedMemberForPermissions] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState(null);
  const [themeColor, setThemeColor] = useState('#c4fffc');
  const [highlightedEmail, setHighlightedEmail] = useState(null);
  const { empresaActiva } = useEmpresa();
  const { user, isSuperAdmin } = useSelector(state => state.auth);

  // Obtener el rol del usuario desde la empresa activa
  const userRole = empresaActiva?.rolUsuario || empresaActiva?.role;


  // Actualizar color del tema en tiempo real
  useEffect(() => {
    const updateThemeColor = () => {
      const color = localStorage.getItem('theme-primary-color') || '#c4fffc';
      setThemeColor(color);
    };

    updateThemeColor();
    const interval = setInterval(updateThemeColor, 200);
    window.addEventListener('theme-color-change', updateThemeColor);

    return () => {
      clearInterval(interval);
      window.removeEventListener('theme-color-change', updateThemeColor);
    };
  }, []);

  // Ocultar header cuando cualquier modal está abierto en móvil
  useEffect(() => {
    const anyModalOpen = modalOpen || catalogModalOpen || permissionsModalOpen || deleteModalOpen;

    if (anyModalOpen) {
      document.body.classList.add('equipo-modal-open');
      document.body.style.overflow = 'hidden';
    } else {
      document.body.classList.remove('equipo-modal-open');
      document.body.style.overflow = '';
    }

    return () => {
      document.body.classList.remove('equipo-modal-open');
      document.body.style.overflow = '';
    };
  }, [modalOpen, catalogModalOpen, permissionsModalOpen, deleteModalOpen]);

  // Manejar highlight de usuario desde Historial
  useEffect(() => {
    if (location.state?.highlightUserEmail && teamMembers.length > 0) {
      setHighlightedEmail(location.state.highlightUserEmail);

      // Hacer scroll al usuario destacado después de un pequeño delay
      setTimeout(() => {
        if (highlightedMemberRef.current) {
          highlightedMemberRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });
        }
      }, 500);

      // Quitar el highlight después de 5 segundos
      setTimeout(() => {
        setHighlightedEmail(null);
      }, 5000);
    }
  }, [location.state, teamMembers]);

  // Verificar si puede editar permisos (admin o editor)
  const canEditPermissions = isSuperAdmin || userRole === 'admin' || userRole === 'editor';
  const canManageTeam = isSuperAdmin || userRole === 'admin' || userRole === 'administrador' || userRole === 'editor';
  const canManageCatalogs = isSuperAdmin || userRole === 'admin' || userRole === 'administrador' || userRole === 'editor'; // Admins y editores pueden gestionar catálogos de cargos
  const canViewDetails = canManageTeam;

  // Contar administradores
  const adminCount = teamMembers.filter(m => m.systemRole === 'admin').length;

  // Función para verificar si es el usuario actual
  const isCurrentUser = (member) => {
    return member.id === user?.id;
  };

  // Función para verificar si puede editar/eliminar un miembro específico
  const canEditMember = (member) => {
    const targetIsAdmin = member.systemRole === 'admin';
    const targetIsEditor = member.systemRole === 'editor';
    const targetIsMember = member.systemRole === 'member';
    const isCurrentUser = member.id === user?.id;

    // Usuario puede editar su propio perfil SIEMPRE
    if (isCurrentUser) {
      return true;
    }

    // SuperAdmin puede editar/eliminar a cualquiera
    if (isSuperAdmin) {
      return true;
    }

    // Si es el último administrador, solo superadmin puede gestionarlo
    if (targetIsAdmin && adminCount === 1) {
      return false;
    }

    // Administradores NO pueden editar/eliminar a otros administradores
    if ((userRole === 'admin' || userRole === 'administrador') && targetIsAdmin) {
      return false;
    }

    // Administradores pueden gestionar editores y miembros (no son ellos mismos)
    if ((userRole === 'admin' || userRole === 'administrador') && (targetIsEditor || targetIsMember)) {
      return true;
    }

    // Editores NO pueden editar/eliminar administradores
    if (userRole === 'editor' && targetIsAdmin) {
      return false;
    }

    // Editores NO pueden editar/eliminar a otros editores
    if (userRole === 'editor' && targetIsEditor) {
      return false;
    }

    // Editores solo pueden gestionar miembros
    if (userRole === 'editor' && targetIsMember) {
      return true;
    }

    // Miembros no pueden editar/eliminar a nadie (excepto a sí mismos, ya manejado arriba)
    return false;
  };

  // Cargar miembros del equipo
  const cargarEquipo = async () => {
    try {
      setLoading(true);
      const response = await api.get('/team/members', {
        params: { companyId: empresaActiva?.id }
      });

      if (response.data.success) {
        const usuariosFormateados = response.data.data.map(member => ({
          id: member.id,
          nombre: `${member.firstName} ${member.lastName}`,
          firstName: member.firstName,  // Guardar nombres originales
          lastName: member.lastName,    // para uso posterior
          cargo: member.position || 'Sin cargo',
          departamento: member.department || '',
          systemRole: member.systemRole || 'miembro',
          email: member.email,
          telefono: member.phone || '',
          ubicacion: member.department || 'Sin departamento',
          fechaIngreso: member.joinDate,
          proyectosActivos: member.activeProjects || 0,
          estado: member.status,
          avatar: member.avatar || `${member.firstName?.[0] || ''}${member.lastName?.[0] || ''}`.toUpperCase(),
          username: member.username || member.email.split('@')[0],
          isAdmin: member.systemRole === 'admin',
          permissions: member.permissions || {}
        }));

        setTeamMembers(usuariosFormateados);
      }
    } catch (error) {
      console.error('Error al cargar equipo:', error);
      setTeamMembers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargarEquipo();
  }, []);

  // Manejar submit del modal
  const handleModalSubmit = async (data) => {
    try {
      if (modalMode === 'add') {
        const response = await api.post('/team/members', {
          ...data,
          companyId: empresaActiva?.id
        });
        if (response.data.success) {
          await cargarEquipo();
          return { success: true };
        }
        return { success: false, message: 'No se pudo agregar el miembro' };
      } else {
        const response = await api.put(`/team/members/${selectedMember.id}?companyId=${empresaActiva?.id}`, data);
        if (response.data.success) {
          await cargarEquipo();
          setSelectedMember(null);
          return { success: true };
        }
        return { success: false, message: 'No se pudo actualizar el miembro' };
      }
    } catch (error) {
      console.error('Error:', error);
      return { success: false, message: error.response?.data?.message || 'Error al procesar la solicitud' };
    }
  };

  // Manejar eliminación de miembro
  const handleDeleteMember = (member) => {
    setMemberToDelete(member);
    setDeleteModalOpen(true);
  };

  // Confirmar eliminación
  const confirmDeleteMember = async () => {
    if (!memberToDelete) return;

    try {
      const response = await api.delete(`/team/members/${memberToDelete.id}?companyId=${empresaActiva?.id}`);
      if (response.data.success) {
        await cargarEquipo();
        setDeleteModalOpen(false);
        setMemberToDelete(null);
      }
    } catch (error) {
      console.error('Error al eliminar miembro:', error);
    }
  };

  const openAddModal = () => {
    setSelectedMember(null);
    setModalMode('add');
    setModalOpen(true);
  };

  const openEditModal = (member) => {
    const memberData = {
      id: member.id,
      firstName: member.firstName || member.nombre.split(' ')[0],  // Usar nombres originales si están disponibles
      lastName: member.lastName || member.nombre.split(' ').slice(1).join(' '),
      email: member.email,
      phone: member.telefono,
      position: member.cargo === 'Sin cargo' ? '' : member.cargo,
      department: member.departamento,
      systemRole: member.systemRole || 'miembro',
      status: member.estado,
      username: member.username,
      permissions: member.permissions
    };
    setSelectedMember(memberData);
    setModalMode('edit');
    setModalOpen(true);
  };

  const openPermissionsModal = (member) => {
    setSelectedMemberForPermissions(member);
    setPermissionsModalOpen(true);
  };

  const handleSavePermissions = async (memberId, newPermissions) => {
    try {
      const response = await api.put(`/team/members/${memberId}/permissions?companyId=${empresaActiva?.id}`, {
        permissions: newPermissions
      });
      if (response.data.success) {
        await cargarEquipo();
      }
    } catch (error) {
      console.error('Error updating permissions:', error);
    }
  };

  const filteredMembers = teamMembers.filter(member => {
    const matchesSearch =
      member.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
      member.cargo.toLowerCase().includes(searchTerm.toLowerCase()) ||
      member.especialidad.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesFilter =
      filterRole === 'todos' ||
      member.cargo.toLowerCase().includes(filterRole.toLowerCase());

    return matchesSearch && matchesFilter;
  });

  const getStatusBadge = (estado) => {
    switch(estado) {
      case 'active': return 'status-active';
      case 'vacation': return 'status-vacation';
      case 'inactive': return 'status-leave';
      default: return 'status-active';
    }
  };

  const getStatusText = (estado) => {
    switch(estado) {
      case 'active': return 'Activo';
      case 'vacation': return 'Vacaciones';
      case 'inactive': return 'Inactivo';
      default: return 'Activo';
    }
  };

  // Si no hay empresa activa, mostrar mensaje
  if (!empresaActiva) {
    return (
      <div className="equipo-container">
        <div className="equipo-header">
          <div className="header-title">
            <UserCheck size={28} />
            <h1>Equipo</h1>
          </div>
        </div>
        <div className="empty-state">
          <Users size={48} />
          <h3>Selecciona una empresa</h3>
          <p>Para ver el equipo de trabajo, primero selecciona una empresa desde el selector de empresas.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="equipo-container">
      <div className="equipo-header">
        <div className="header-title">
          <UserCheck size={28} />
          <h1>Equipo - {empresaActiva.nombreFantasia}</h1>
        </div>
        <div className="header-actions">
          {canManageCatalogs && (
            <button
              className="btn-settings"
              onClick={() => setCatalogModalOpen(true)}
              title="Gestionar Cargos y Permisos"
              style={{
                backgroundColor: '#fff',
                color: '#000',
                border: '2px solid #000',
                padding: '10px 16px',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                fontFamily: "'Space Grotesk', sans-serif"
              }}
            >
              <Settings size={20} />
              Gestionar
            </button>
          )}
          {canManageTeam && (
            <button
              className="btn-nuevo-miembro"
              onClick={openAddModal}
              style={{
                backgroundColor: themeColor,
                color: '#000',
                border: '2px solid #000',
                padding: '10px 20px',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                fontFamily: "'Space Grotesk', sans-serif"
              }}
            >
              <Plus size={20} />
              Nuevo Miembro
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="loading-state">
          <p>Cargando equipo...</p>
        </div>
      ) : (
        <>
          <div className="equipo-stats">
        <div className="stat-card">
          <div className="stat-icon" style={{
            width: '48px',
            height: '48px',
            backgroundColor: themeColor,
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '2px solid #000'
          }}>
            <Users size={20} color="#000" />
          </div>
          <div className="stat-content">
            <p>Total Miembros</p>
            <h3>{teamMembers.length}</h3>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{
            width: '48px',
            height: '48px',
            backgroundColor: '#90EE90',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '2px solid #000'
          }}>
            <UserCheck size={20} color="#000" />
          </div>
          <div className="stat-content">
            <p>Activos</p>
            <h3>{teamMembers.filter(m => m.estado === 'active').length}</h3>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{
            width: '48px',
            height: '48px',
            backgroundColor: '#FFE4B5',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '2px solid #000'
          }}>
            <Clock size={20} color="#000" />
          </div>
          <div className="stat-content">
            <p>En Vacaciones</p>
            <h3>{teamMembers.filter(m => m.estado === 'vacation').length}</h3>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{
            width: '48px',
            height: '48px',
            backgroundColor: '#E6E6FA',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '2px solid #000'
          }}>
            <Award size={20} color="#000" />
          </div>
          <div className="stat-content">
            <p>Proyectos Activos</p>
            <h3>{teamMembers.reduce((sum, m) => sum + m.proyectosActivos, 0)}</h3>
          </div>
        </div>
      </div>

      <div className="equipo-controls">
        <div className="search-box">
          <Search size={20} />
          <input
            type="text"
            placeholder="Buscar por nombre, cargo o especialidad..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="controls-right">
          <CustomSelect
            name="filterRole"
            value={filterRole}
            onChange={(e) => setFilterRole(e.target.value)}
            options={[
              { value: 'todos', label: 'Todos los cargos' },
              { value: 'administrador', label: 'Administradores' },
              { value: 'vendedor', label: 'Vendedores' },
              { value: 'contador', label: 'Contadores' },
              { value: 'coordinador', label: 'Coordinadores' },
              { value: 'especialista', label: 'Especialistas' },
              { value: 'supervisor', label: 'Supervisores' },
              { value: 'asistente', label: 'Asistentes' },
              { value: 'analista', label: 'Analistas' }
            ]}
            placeholder="Todos los cargos"
          />
        </div>
      </div>

      <div className="equipo-grid">
        {filteredMembers.map((member) => (
          <div
            key={member.id}
            ref={member.email === highlightedEmail ? highlightedMemberRef : null}
            className={`member-card ${member.email === highlightedEmail ? 'member-card-highlighted' : ''}`}>
            <div className="member-header">
              <div className="member-avatar" style={{
                backgroundColor: themeColor,
                border: '2px solid #000',
                color: '#000'
              }}>
                <span>{member.avatar}</span>
              </div>
              <div className="member-status">
                <span className={`status ${getStatusBadge(member.estado)}`} style={{
                  backgroundColor: member.estado === 'active' ? themeColor : member.estado === 'vacation' ? '#FFE082' : '#E0E0E0',
                  color: '#000',
                  padding: '4px 12px',
                  borderRadius: '4px',
                  fontSize: '12px',
                  fontWeight: '600',
                  textTransform: 'uppercase',
                  fontFamily: "'Space Grotesk', sans-serif",
                  border: '1px solid #000'
                }}>
                  {getStatusText(member.estado)}
                </span>
              </div>
            </div>

            <div className="member-info">
              <h3>{member.nombre}</h3>
              <p className="member-role">{member.cargo}</p>
              {member.departamento && <p className="member-department">{member.departamento}</p>}
              <p className="member-permission-role">
                <span className={`role-badge ${member.systemRole}`} style={{
                  backgroundColor: member.systemRole === 'admin' ? '#000' : themeColor,
                  color: member.systemRole === 'admin' ? themeColor : '#000',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  fontSize: '11px',
                  fontWeight: '600',
                  fontFamily: "'Space Grotesk', sans-serif",
                  border: '1px solid #000'
                }}>
                  {member.systemRole === 'admin' ? 'Administrador' :
                   member.systemRole === 'editor' ? 'Editor' :
                   member.systemRole === 'member' ? 'Miembro' : 'Miembro'}
                </span>
              </p>
            </div>

            <div className="member-contact">
              <div className="contact-item">
                <Mail size={16} />
                <span>{member.email}</span>
              </div>
              <div className="contact-item">
                <Phone size={16} />
                <span>{member.telefono}</span>
              </div>
              <div className="contact-item">
                <MapPin size={16} />
                <span>{member.ubicacion}</span>
              </div>
            </div>

            <div className="member-stats">
              <div className="stat-item">
                <Calendar size={14} />
                <span>Desde: {new Date(member.fechaIngreso).toLocaleDateString('es-CL')}</span>
              </div>
            </div>

            <div className="member-actions">
              {/* Botón de editar */}
              {canEditMember(member) && (
                <button
                  className="btn-action"
                  title="Editar"
                  onClick={() => openEditModal(member)}
                >
                  <Edit size={16} />
                </button>
              )}
              {/* Botón de permisos solo para miembros Y solo si el usuario actual puede editarlos */}
              {member.systemRole === 'member' && canEditMember(member) && canEditPermissions && (
                <button
                  className="btn-action btn-permissions"
                  title="Configurar permisos"
                  onClick={() => openPermissionsModal(member)}
                >
                  <Shield size={16} />
                </button>
              )}
              {/* Botón de eliminar */}
              {canEditMember(member) && !isCurrentUser(member) && (
                <button
                  className="btn-action"
                  title="Eliminar"
                  onClick={() => handleDeleteMember(member)}
                >
                  <Trash2 size={16} />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
        </>
      )}

      {/* Team Modal */}
      <TeamModal
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setSelectedMember(null);
        }}
        mode={modalMode}
        member={selectedMember}
        onSubmit={handleModalSubmit}
        companyId={empresaActiva?.id}
        isEditingSelf={selectedMember && selectedMember.id === user?.id}
        currentUserRole={userRole}
      />

      {/* Catalog Modal */}
      <CatalogModal
        isOpen={catalogModalOpen}
        onClose={() => setCatalogModalOpen(false)}
        companyId={empresaActiva?.id}
      />

      {/* Permissions Modal */}
      <PermissionsModal
        isOpen={permissionsModalOpen}
        onClose={() => setPermissionsModalOpen(false)}
        member={selectedMemberForPermissions}
        onSave={handleSavePermissions}
        currentUserRole={userRole}
        isSuperAdmin={isSuperAdmin}
      />

      {/* Delete Confirmation Modal */}
      {deleteModalOpen && (
        <div className="team-modal-overlay" onClick={() => setDeleteModalOpen(false)}>
          <div className="team-modal delete-confirmation-modal" onClick={(e) => e.stopPropagation()}>
            <div className="team-modal-header">
              <div className="team-modal-title">
                <Trash2 size={24} />
                <h2>Confirmar Eliminación</h2>
              </div>
              <button className="team-modal-close" onClick={() => setDeleteModalOpen(false)}>
                <X size={24} />
              </button>
            </div>

            <div className="delete-modal-content">
              <div className="delete-warning">
                <div className="warning-icon">
                  <Trash2 size={48} />
                </div>
                <h3>¿Estás seguro de eliminar a este usuario?</h3>
                {memberToDelete && (
                  <div className="member-info">
                    <p><strong>{memberToDelete.nombre}</strong></p>
                    <p>{memberToDelete.email}</p>
                    <p className="warning-text">
                      Esta acción no se puede deshacer. El usuario será eliminado permanentemente del sistema.
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className="team-modal-footer">
              <button
                type="button"
                className="team-btn-cancel"
                onClick={() => setDeleteModalOpen(false)}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="team-btn-delete"
                onClick={confirmDeleteMember}
              >
                Eliminar Usuario
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Equipo;